import { Link } from 'react-router-dom';
import { Calendar, Clock, ArrowRight } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/common/Card';
import Badge from '@/components/common/Badge';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import { useUpcomingEvents } from '@/hooks/useEvents';

const EVENT_TYPE_COLORS = {
  deadline: 'bg-red-500',
  appointment: 'bg-blue-500',
  sale_end: 'bg-purple-500',
  reminder: 'bg-yellow-500',
};

const EVENT_TYPE_LABELS = {
  deadline: 'Deadline',
  appointment: 'Appointment',
  sale_end: 'Sale Ends',
  reminder: 'Reminder',
};

export default function EventsWidget() {
  const { data: events, isLoading } = useUpcomingEvents(7);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-blue-500" />
            Upcoming Events
          </CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Next 7 days
          </p>
        </div>
        {events && events.length > 0 && (
          <Link
            to="/events"
            className="flex items-center gap-1 text-sm font-medium text-primary hover:underline"
          >
            View all
            <ArrowRight className="h-4 w-4" />
          </Link>
        )}
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="py-12">
            <LoadingSpinner />
          </div>
        ) : events && events.length > 0 ? (
          <div className="space-y-3">
            {events.slice(0, 5).map((event) => {
              const eventDate = new Date(event.event_date);
              const isToday =
                eventDate.toDateString() === new Date().toDateString();
              const isTomorrow =
                eventDate.toDateString() ===
                new Date(Date.now() + 86400000).toDateString();

              let dateLabel = eventDate.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
              });

              if (isToday) dateLabel = 'Today';
              if (isTomorrow) dateLabel = 'Tomorrow';

              const timeLabel = eventDate.toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
              });

              return (
                <Link
                  key={event.id}
                  to="/events"
                  className={`block p-3 rounded-lg border transition-colors ${
                    isToday
                      ? 'bg-blue-50 dark:bg-blue-950/20 border-blue-300 dark:border-blue-700 hover:bg-blue-100 dark:hover:bg-blue-950/30'
                      : 'hover:bg-accent'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {/* Date Indicator */}
                    <div
                      className={`flex-shrink-0 w-12 h-12 rounded-lg flex flex-col items-center justify-center ${
                        EVENT_TYPE_COLORS[event.event_type] || 'bg-gray-500'
                      } text-white`}
                    >
                      <div className="text-xs font-medium uppercase">
                        {eventDate.toLocaleDateString('en-US', { month: 'short' })}
                      </div>
                      <div className="text-lg font-bold leading-none">
                        {eventDate.getDate()}
                      </div>
                    </div>

                    {/* Event Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-medium text-sm line-clamp-2">
                          {event.title}
                        </p>
                      </div>

                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        {isToday && (
                          <Badge variant="default" className="text-xs">
                            Today
                          </Badge>
                        )}
                        <Badge variant="outline" className="text-xs">
                          {EVENT_TYPE_LABELS[event.event_type] || event.event_type}
                        </Badge>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Clock className="h-3 w-3" />
                          {!isToday && !isTomorrow && `${dateLabel}, `}
                          {timeLabel}
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8">
            <Calendar className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">No upcoming events</p>
            <p className="text-xs text-muted-foreground mt-1">
              Events will appear here automatically
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
